import * as React from 'react';
import { Translate, I18n } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../reducers';
import { Wrapper, StyledButton } from './styles';
import Menu, { MenuConfig } from '../DropdownMenu';
import ImportDesktopFile from '../../containers/ImportDesktopFile';
import { openPanel as showFolderCreator } from '../../containers/FolderCreationPanel/actions';
import { openPanel as showExternalLinkCreator } from '../../containers/ExternalLinkCreationPanel/actions';
import { showProposalCreator } from '../../containers/ProposalCreator/actions';
import { ProposalSource } from '../../containers/ProposalCreator/reducer';
import { CreateButtonOption, MenuPosition } from '../../typings/menu';
import { StyledMenuItem, StyledMenuItemLink } from '../MenuManager/Left/MenuItem/styles';
import Utils from '../../utils';

interface ButtonCreateProps {
	options: CreateButtonOption[];
	position: MenuPosition;
	dispatch: Dispatch<StoreState>;
	onOptionClick?: () => void;
}

const ButtonCreate = ({
	options,
	position,
	dispatch,
	onOptionClick,
}: ButtonCreateProps) => {
	const wrapperElement = React.useRef<HTMLDivElement>(null);
	const [isMenuDisplayed, setIsMenuDisplayed] = React.useState(false);

	const handleOptionClick = () => {
		setIsMenuDisplayed(false);

		if (onOptionClick) {
			onOptionClick();
		}
	};

	const config: MenuConfig = {
		[CreateButtonOption.folder]: {
			label: I18n.t('messages.create_folder'),
			icon: 'iconset_uprez-create_folder',
			onClick: () => {
				dispatch(showFolderCreator());
				handleOptionClick();
			},
			hasSeparator: true,
		},
		[CreateButtonOption.proposalFromTemplate]: {
			label: I18n.t('messages.create_proposal_from_template'),
			icon: 'iconset_uprez-create_proposal_template',
			onClick: () => {
				dispatch(showProposalCreator(ProposalSource.TEMPLATE));
				handleOptionClick();
			},
		},
		[CreateButtonOption.proposalFromFile]: {
			label: I18n.t('messages.create_proposal_from_file'),
			icon: 'iconset_uprez-create_proposal_file',
			onClick: () => {
				dispatch(showProposalCreator(ProposalSource.FILE));
				handleOptionClick();
			},
			hasSeparator: true,
		},
		[CreateButtonOption.importFile]: {
			label: I18n.t('messages.import_file'),
			component: <ImportDesktopFile onClick={handleOptionClick} />,
		},
		[CreateButtonOption.externalLink]: {
			label: I18n.t('messages.create_external_link'),
			icon: 'iconset_uprez-create_ext_link',
			onClick: () => {
				dispatch(showExternalLinkCreator());
				handleOptionClick();
			},
		},
	};

	React.useEffect(() => {
		const handleClickOutside = (e: MouseEvent | TouchEvent) => {
			if (wrapperElement.current && !wrapperElement.current.contains(e.target as HTMLElement)) {
				setIsMenuDisplayed(false);
			}
		};

		window.addEventListener('click', handleClickOutside);

		return () => {
			window.removeEventListener('click', handleClickOutside);
		};
	}, []);

	if (options.length === 0) {
		return null;
	}

	const filteredConfig = Object.keys(config).reduce((acc, current) => {
		if (options.includes(current as CreateButtonOption)) {
			acc[current] = config[current];
		}
		return acc;
	}, {});

	return (
		<Wrapper
			ref={wrapperElement}
			position={position}
		>
			{position === MenuPosition.burger
				? (
					<StyledMenuItem
						isActive={isMenuDisplayed}
						onClick={() => setIsMenuDisplayed(prevState => !prevState)}
					>
						<StyledMenuItemLink>
							{Utils.getSvgIcon('iconset_plus')}
							<Translate value="messages.create" />
						</StyledMenuItemLink>
					</StyledMenuItem>
				)
				: (
					<StyledButton
						type="button"
						isActive={isMenuDisplayed}
						onClick={() => setIsMenuDisplayed(prevState => !prevState)}
					>
						<Translate value="messages.create" />
					</StyledButton>
				)
			}
			<Menu
				config={filteredConfig}
				isDisplayed={isMenuDisplayed}
				position={position}
			/>
		</Wrapper>
	);
};

export default ButtonCreate;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ButtonCreate/index.tsx